import { useContext, useMemo } from "react";

//Components
import CheckboxWithLabel from "./CheckboxWithLabel";
import { FormControl, FormLabel } from "@material-ui/core";
import WrappedTextareaAutosize from "./WrappedTextareaAutosize";
import WrappedTextField from "./WrappedTextField";

//Contexts
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { UserContext } from "../Contexts/UserContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { MutationContext } from "../Contexts/MutationContext";

//utils
import PropTypes from "prop-types";
import { shouldBeLocked } from "../utils/Roles";

//styling
import { useStyles } from "../Styles/styles";
import deleteButton from "../Assets/deleteButton.png";

const MRI = ({
  nic = {},
  nicEntryIndex = 0,
  numOfNicEntries = 1,
  incidentId = "",
  userId = "",
  incidentIndex = -1,
  handleAddNicEntry = () =>
    alert("No handleAddNicEntry function passed to MRI"),
  disabled = false,
  userRoles = [],
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles]
  );
  const classes = useStyles();
  const { incidentDispatch } = useContext(IncidentsContext);
  const { nightMode } = useContext(UserContext);
  const { REMOVE_NIC, UPDATE_NIC } = INCIDENT_ACTIONS;
  const { addOrUpdateNicEntry, removeNicEntry } = useContext(MutationContext);

  const stateToMutation = {
    mriNumber: "mriNumber",
    nicNumber: "nicNumber",
    narrative: "narrative",
    cancelled: "cancelled",
  };

  const handleChange = (e, submit = true) => {
    let name;
    let value;
    if (e.target.type === "checkbox") {
      value = e.target.checked;
      name = e.target.value;
    } else {
      name = e.target.name;
      value = e.target.value;
    }

    const fieldToUpdate = stateToMutation[name];
    incidentDispatch({
      type: UPDATE_NIC,
      payload: {
        incidentIndex: incidentIndex,
        nicEntryIndex: nicEntryIndex,
        property: name,
        value: value,
      },
    });
    submit &&
      addOrUpdateNicEntry({
        variables: {
          input: {
            incidentId: incidentId,
            nicEntryId: nic.id,
            originatorId: userId,
            [fieldToUpdate]: value,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });

    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`
      );
      return null;
    }
  };

  const handleRemoveNicEntry = () => {
    if (window.confirm("Are you sure you want to remove this entry?")) {
      removeNicEntry({
        variables: { input: { nicId: nic.id, originatorId: userId } },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      incidentDispatch({
        type: REMOVE_NIC,
        payload: { incidentIndex: incidentIndex, nicEntryIndex: nicEntryIndex },
      });
      if (numOfNicEntries === 1) {
        handleAddNicEntry();
      }
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        width: "100%",
        border: "1px solid grey",
        borderRadius: "5px",
        marginBottom: "4vh",
      }}
      autoComplete="off"
      onSubmit={() => console.log("Handle submit")}
    >
      <FormControl
        className={classes.formControl}
        style={{ marginRight: "4vw" }}
      >
        <WrappedTextField
          disabled={memoizedLocked}
          value={nic?.mriNumber || ""}
          onChange={handleChange}
          id={"MRI-number-" + nic.id}
          InputLabelProps={{ shrink: true }}
          label="MRI Number"
          name={"mriNumber"}
        />
      </FormControl>
      <FormControl
        className={classes.formControl}
        style={{ marginRight: "4vw" }}
      >
        <WrappedTextField
          disabled={memoizedLocked}
          InputLabelProps={{ shrink: true }}
          id={"NIC-number-" + nic.id}
          label="NCIC NIC#"
          value={nic?.nicNumber || ""}
          name={"nicNumber"}
          onChange={e => handleChange(e, false)}
          onBlur={e => handleChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
        />
      </FormControl>
      <FormControl
        className={classes.formControl}
        style={{ marginRight: "4vw", flexGrow: "2" }}
      >
        <FormLabel component="label">{"Narrative"}</FormLabel>
        <WrappedTextareaAutosize
          disabled={memoizedLocked}
          dataCy="narrative"
          className={nightMode ? classes.textAreaDark : classes.textAreaLight}
          margin="dense"
          id="narrative-id"
          value={nic?.narrative || ""}
          minRows={1}
          maxRows={10}
          name="narrative"
          onChange={e => handleChange(e, false)}
          onBlur={e => handleChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
        />
      </FormControl>
      <CheckboxWithLabel
        formStyle={classes.formControl}
        checked={nic?.cancelled || false}
        onChange={handleChange}
        stateName={"cancelled"}
        title={"Cleared"}
        {...props}
      />
      <FormControl
        style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        className={classes.formControl}
      >
        <button
          type="button"
          onClick={!memoizedLocked ? () => handleRemoveNicEntry() : null}
          disabled={memoizedLocked}
          style={{ background: "none", border: 0, padding: 0 }}
        >
          <img
            data-cy={"delete-nic-entry-button-" + nic?.id}
            src={deleteButton}
            className={classes.pngButton}
            alt="Delete NIC Entry"
          />
        </button>
      </FormControl>
    </div>
  );
};

MRI.propTypes = {
  nic: PropTypes.object,
  nicEntryIndex: PropTypes.number,
  numOfNicEntries: PropTypes.number,
  incidentId: PropTypes.string,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  incidentIndex: PropTypes.number,
  handleAddNicEntry: PropTypes.func,
  disabled: PropTypes.bool,
  userRoles: PropTypes.array,
};

export default MRI;
